import { useRef } from 'react'
import { Box, Button } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { QRCodeCanvas } from 'qrcode.react'
import { useTranslation } from 'react-i18next'

type StudyQrProps = {
  /** The value encoded by the QR — typically the respondent share link. */
  value: string
  /** On-screen render size in px (the backing canvas is always high-res). */
  size?: number
  /** File name used for the PNG download. */
  fileName?: string
  /** Hide the built-in download button (e.g. when the parent renders its own). */
  showDownload?: boolean
}

/**
 * A real, scannable QR code rendered to a canvas, with one-click PNG download.
 * The canvas is rendered at a high backing resolution and scaled down for
 * display, so the downloaded PNG is crisp enough to print.
 */
export default function StudyQr({ value, size = 160, fileName = 'study-qr.png', showDownload = true }: StudyQrProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const wrapRef = useRef<HTMLDivElement>(null)

  const downloadPng = () => {
    const canvas = wrapRef.current?.querySelector('canvas')
    if (!canvas) return
    const link = document.createElement('a')
    link.href = canvas.toDataURL('image/png')
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <Box
        ref={wrapRef}
        sx={{
          p: 1.25,
          bgcolor: '#fff',
          border: `1px solid ${c.ink08}`,
          borderRadius: '12px',
          lineHeight: 0,
        }}
      >
        <QRCodeCanvas
          value={value}
          size={1024}
          marginSize={2}
          level="M"
          bgColor="#ffffff"
          fgColor="#101012"
          style={{ width: size, height: size, display: 'block' }}
        />
      </Box>
      {showDownload && (
        <Button
          onClick={downloadPng}
          color="inherit"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />}
          sx={{ color: c.ink72, fontSize: 13 }}
        >
          {t('wizard.distribution.qrDownload')}
        </Button>
      )}
    </Box>
  )
}
